import { useEffect, useState } from 'react';

/**
 * The mobile shell covers phones and tablets. A coarse pointer catches a tablet
 * in landscape, where the width alone would still look like the desktop shell.
 */
export const MOBILE_MEDIA_QUERY = '(max-width: 800px), (pointer: coarse)';

function matches(): boolean {
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
}

export function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(matches);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onChange = (): void => setMobile(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return mobile;
}

/** Keep fixed dialogs inside the area that stays visible above the keyboard. */
export function useVisualViewport(): void {
  useEffect(() => {
    const viewport = window.visualViewport;
    const root = document.documentElement;
    if (!viewport) return;

    const sync = (): void => {
      root.style.setProperty('--vv-top', `${viewport.offsetTop}px`);
      root.style.setProperty('--vv-height', `${viewport.height}px`);
    };
    const onFocus = (event: FocusEvent): void => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !target.closest('.overlay')) return;
      window.setTimeout(() => target.scrollIntoView({ block: 'nearest' }), 50);
    };

    sync();
    viewport.addEventListener('resize', sync);
    viewport.addEventListener('scroll', sync);
    document.addEventListener('focusin', onFocus);
    return () => {
      viewport.removeEventListener('resize', sync);
      viewport.removeEventListener('scroll', sync);
      document.removeEventListener('focusin', onFocus);
      root.style.removeProperty('--vv-top');
      root.style.removeProperty('--vv-height');
    };
  }, []);
}
